import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { base_url } from '../../Utils/baseUrl';
import { useLocation, useNavigate } from 'react-router-dom';
import './verifyotp.css'
import logo from '../../logo.svg'
import commonFunction from '../../Utils/commonFunction';
import Loader from '../Common/Loader';

const OTP_EXPIRY_KEY = "otp_expiry_time";
const SUCCESS_REDIRECT_DELAY = 3000;

function VerifyOTP() {

    const [timeLeft, setTimeLeft] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verificationState, setVerificationState] = useState(null);
    const [verificationMessage, setVerificationMessage] = useState('');
    const [redirectTimeLeft, setRedirectTimeLeft] = useState(SUCCESS_REDIRECT_DELAY);

    useEffect(() => {
        window.scrollTo(0, 0)
    }, []);

    const location = useLocation();
    const email = location.state || null;
    const navigate = useNavigate();
    const validationSchema = yup.object({
        otp: yup.string().required('Engangskode som er tilsendt på mail er nødvendig').length(6, 'Engangskoden skal bestå af 6 cifre'),
    });

    const formik = useFormik({
        initialValues: {
            email: email?.email,
            otp: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setVerificationState('verifying');
            setVerificationMessage('');

            try {
                const res = await axios.post(`${base_url}auth/verify-otp`, values);
                const successMessage = res.data?.message;
                setVerificationMessage(
                    typeof successMessage === 'string' && successMessage
                        ? successMessage
                        : 'Your OTP has been verified successfully.'
                );
                setRedirectTimeLeft(SUCCESS_REDIRECT_DELAY);
                setVerificationState('success');
            } catch (error) {
                const apiMessage = error?.response?.data?.message;
                setVerificationMessage(
                    typeof apiMessage === 'string' && apiMessage
                        ? apiMessage
                        : 'Unable to verify OTP. Please try again.'
                );
                setVerificationState('error');
            }
        },
    });

    useEffect(() => {
        if (verificationState !== 'success') {
            return undefined;
        }

        const redirectAt = Date.now() + SUCCESS_REDIRECT_DELAY;
        const interval = setInterval(() => {
            const remaining = Math.max(0, redirectAt - Date.now());
            setRedirectTimeLeft(remaining);

            if (remaining === 0) {
                clearInterval(interval);
                navigate('/login', { replace: true });
            }
        }, 50);

        return () => clearInterval(interval);
    }, [verificationState, navigate]);

    // Redirect to home if email is not found
    useEffect(() => {
        if (!email) {
            navigate('/');
        }
    }, [email, navigate]);


    // Initialize Timer
    useEffect(() => {
        let expiry = localStorage.getItem(OTP_EXPIRY_KEY);

        if (!expiry) {
            expiry = Date.now() + 10 * 60 * 1000; // 3 mins
            localStorage.setItem(OTP_EXPIRY_KEY, expiry);
        }

        updateTimer(expiry);

        const interval = setInterval(() => {
            updateTimer(expiry);
        }, 1000);

        return () => clearInterval(interval);
    }, [timeLeft]);

    // Timer Calculation
    const updateTimer = (expiry) => {
        const remaining = Math.max(0, expiry - Date.now());

        setTimeLeft(remaining);

        if (remaining === 0) {
            setCanResend(true);
        }
    };

    // Format Time
    const formatTime = (ms) => {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const handleResend = async () => {
        try {
            setLoading(true);
            // Resend API call
            const res = await commonFunction.reSendOtp(email?.email);
            if (res.status === true) {
                toast.success("Ny kode tilsendt til mailen");
                const newExpiry = Date.now() + 10 * 60 * 1000;
                localStorage.setItem(OTP_EXPIRY_KEY, newExpiry);

                setCanResend(false);
                setTimeLeft(newExpiry - Date.now());
            } else {
                toast.error(`Something went wrong, ${res?.data?.message}`);
                setCanResend(true);
                setTimeLeft(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Loader />
        )
    }
    return (
        <React.Fragment>
            <main>
                <section className="verify-otp-sec wow pulse">
                    <div className="container">
                        <div className="verify-otp-inner">
                            <div className="row">
                                <div className="col-md-12">
                                    <form id="verifyform" className="form_elements" onSubmit={formik.handleSubmit}>
                                        <div id="verifyotptitle" className="form-title">
                                            <img src={logo} alt='not found' />
                                            <p>
                                                ✅ Din bruger er oprettet <br />
                                                Tak fordi du har valgt Onlineskolemad <br />
                                                Vi har sendt en 6-cifret engangskode <br /> til <strong> {email?.email || 'onlinescolmed@gmail.com'} </strong> <br />
                                                <br />
                                                <ul className='p-0'>
                                                    <li>
                                                        1. Indtast koden fra din e-mail
                                                    </li>
                                                    <li>
                                                        2. Tryk på Godkend
                                                    </li>
                                                </ul>
                                                Kan du ikke finde koden? <br />
                                                Tjek din indbakke eller mappen for uønsket mail/spam. <br />
                                                Har du brug for hjælp? <br />
                                                Se vores korte <a href="https://www.youtube.com/watch?v=Q6nLUI3KCHE&t=93s" target='_blank'>videoguide her</a>.
                                            </p>
                                        </div>
                                        <div id="verifyotpformgroup" className="form-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="otp"
                                                name="otp"
                                                placeholder="Indtast venligst engangsadgangskoden"
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^[0-9]*$/.test(value)) {
                                                        formik.handleChange(e);
                                                    }
                                                }}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.otp}
                                                pattern="[0-9]*"
                                            />
                                            {formik.touched.otp && formik.errors.otp ? (
                                                <div className="error-message">{formik.errors.otp}</div>
                                            ) : null}
                                        </div>
                                        <div className="btn_submit">
                                            <button type="submit" className="btn btn-1 w-100" disabled={verificationState === 'verifying'}>
                                                Godkend
                                            </button>
                                        </div>
                                        <div className="otp-actions">
                                            <p className="otp-timer">
                                                Modtog du ikke koden? Anmod om en ny kode om <span>{formatTime(timeLeft)}</span>
                                            </p>
                                            <button type="button" className={`resend-btn ${canResend ? "active" : "disabled"}`}
                                                onClick={handleResend}
                                                disabled={!canResend}
                                            >
                                                {canResend ? "Send ny kode" : ""}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            {verificationState && (
                <div className="otp-verification-overlay">
                    <section
                        className={`otp-verification-popup otp-verification-${verificationState}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="otp-verification-title"
                        aria-describedby="otp-verification-message"
                    >
                        {verificationState === 'verifying' && (
                            <>
                                <span className="otp-verification-spinner" aria-hidden="true" />
                                <h2 id="otp-verification-title">Bekræfter OTP...</h2>
                                <p id="otp-verification-message">
                                    Vent venligst, vi bekræfter din kode. <br />
                                    Du bliver videresendt, når bekræftelsen er gennemført.
                                </p>
                            </>
                        )}
                        {verificationState === 'success' && (
                            <>
                                <span className="otp-verification-success-icon" aria-hidden="true">✓</span>
                                <h2 id="otp-verification-title">Success!</h2>
                                <p id="otp-verification-message">{verificationMessage} <br /> Vi sender dig Videre til din Konto ...</p>
                                <div
                                    className="otp-verification-progress"
                                    role="progressbar"
                                    aria-label="Redirect progress"
                                    aria-valuemin="0"
                                    aria-valuemax={SUCCESS_REDIRECT_DELAY}
                                    aria-valuenow={SUCCESS_REDIRECT_DELAY - redirectTimeLeft}
                                >
                                    <span
                                        style={{
                                            width: `${((SUCCESS_REDIRECT_DELAY - redirectTimeLeft) / SUCCESS_REDIRECT_DELAY) * 100}%`,
                                        }}
                                    />
                                </div>
                                <p className="otp-verification-redirect">
                                    Du bliver videresendt om {Math.ceil(redirectTimeLeft / 1000)} sekunder...
                                </p>
                            </>
                        )}
                        {verificationState === 'error' && (
                            <>
                                <span className="otp-verification-error-icon" aria-hidden="true">!</span>
                                <h2 id="otp-verification-title">Verification failed</h2>
                                <p id="otp-verification-message">{verificationMessage}</p>
                                <button
                                    type="button"
                                    className="otp-verification-close"
                                    onClick={() => setVerificationState(null)}
                                >
                                    Close
                                </button>
                            </>
                        )}
                    </section>
                </div>
            )}
        </React.Fragment>
    );
}

export default VerifyOTP;
